import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  type Query,
  type QueryClient,
  type QueryKey,
  type StaleTime,
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import type {
  AlertListResponse,
  AlertResponse,
} from "@bw/shared/types/generated";

import {
  acknowledgeAlert,
  assignAlert,
  getAlert,
  listAdminRoster,
  listAlerts,
  muteAlert,
  unmuteAlert,
  type AdminRosterResponse,
  type AlertFilters,
} from "../api";
import {
  AssignAlertDialog,
  MUTE_WINDOWS,
  MuteAlertDialog,
  candidateIdentity,
  duplicateCandidateNames,
  isEligibleCandidate,
  type DialogTarget,
} from "../components/AlertActionDialogs";
import {
  AlertFilterBar,
  AlertQueue,
  type Severity,
  type Status,
} from "../components/AlertQueue";
import {
  alertRecoveryStoreFor,
  useAlertRecovery,
  type AlertActionRecovery,
  type AlertActionVariables,
  type ConfirmedMuteEvidence,
} from "../state/alertRecoveryStore";

type AlertActionError = Readonly<{
  alertId: string;
  kind: AlertActionVariables["kind"];
  message: string;
}>;

type ReconciliationResult = Readonly<{
  activeListsCoherent: boolean;
  authoritative: boolean;
  desiredState: DesiredStateTruth;
  invalidationsSucceeded: boolean;
}>;

type DesiredStateTruth = "absent" | "ambiguous" | "present";

const ALERT_ACTION_MUTATION_KEY = ["platform", "alerts", "action"] as const;
const ROSTER_QUERY_KEY = ["platform", "admin-access", "roster"] as const;
const EXACT_ALERT_QUERY_NAMESPACE = ["platform", "alert-authority"] as const;
const ALERTS_REFRESH_REQUIRED =
  "The alert list is out of date. Refresh it before trying this action.";
const ROSTER_REFRESH_REQUIRED =
  "The assignment roster is out of date. Refresh it before assigning.";
const ACTION_OUTCOME_UNCERTAIN =
  "We couldn't confirm the action outcome. The current alert state does not show the requested change.";
const RECONCILIATION_REQUIRED =
  "We couldn't confirm the action outcome because current alert state could not be verified. Refresh before taking another action.";

function isStaleByEffectiveTime<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(query: Query<TQueryFnData, TError, TData, TQueryKey>): boolean {
  const staleTimeOption = (
    query.options as typeof query.options & {
      staleTime?: StaleTime<TQueryFnData, TError, TData, TQueryKey>;
    }
  ).staleTime;
  const staleTime =
    typeof staleTimeOption === "function"
      ? staleTimeOption(query)
      : staleTimeOption;
  return query.isStaleByTime(staleTime);
}

function queryIsAuthoritative<
  TQueryFnData,
  TError,
  TData,
  TQueryKey extends QueryKey,
>(
  query: Query<TQueryFnData, TError, TData, TQueryKey> | undefined,
): query is Query<TQueryFnData, TError, TData, TQueryKey> {
  const state = query?.state;
  return Boolean(
    query &&
      state?.data !== undefined &&
      state.status === "success" &&
      state.fetchStatus === "idle" &&
      state.error === null &&
      state.fetchFailureReason === null &&
      !state.isInvalidated &&
      !isStaleByEffectiveTime(query),
  );
}

function useExactQueryState<TQueryFnData>(
  queryClient: QueryClient,
  queryKey: QueryKey,
) {
  const queryHash = useMemo(
    () => queryClient.defaultQueryOptions({ queryKey }).queryHash,
    [queryClient, queryKey],
  );
  const subscribe = useCallback(
    (notify: () => void) =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryHash === queryHash) notify();
      }),
    [queryClient, queryHash],
  );
  const snapshot = useCallback(() => {
    const query = queryClient
      .getQueryCache()
      .find<TQueryFnData>({ queryKey, exact: true });
    const state = query?.state;
    return [
      state?.dataUpdatedAt,
      state?.dataUpdateCount,
      state?.errorUpdateCount,
      state?.fetchFailureCount,
      state?.fetchStatus,
      state?.isInvalidated,
      state?.status,
      query ? isStaleByEffectiveTime(query) : true,
    ].join(":");
  }, [queryClient, queryKey]);
  useSyncExternalStore(subscribe, snapshot, snapshot);
  return queryClient.getQueryState<TQueryFnData>(queryKey);
}

function isActionAllowed(status: Status, kind: AlertActionVariables["kind"]) {
  if (kind === "acknowledge") return status === "open";
  if (kind === "unmute") return status === "muted";
  return status === "open" || status === "acknowledged";
}

function parseServerTimestamp(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function deriveConfirmedMuteEvidence(
  response: AlertResponse,
  variables: AlertActionVariables,
): ConfirmedMuteEvidence | null {
  const serverMutedUntil = response.muted_until;
  if (
    variables.kind !== "mute" ||
    response.alert_id !== variables.alertId ||
    response.status !== "muted" ||
    !serverMutedUntil
  ) {
    return null;
  }
  const updatedAt = parseServerTimestamp(response.updated_at);
  const mutedUntil = parseServerTimestamp(serverMutedUntil);
  if (
    updatedAt === null ||
    mutedUntil === null ||
    mutedUntil - updatedAt !== variables.minutes * 60_000
  ) {
    return null;
  }
  return Object.freeze({
    alertId: variables.alertId,
    mutedUntil: serverMutedUntil,
  });
}

function actionDesiredStateTruth(
  alert: AlertResponse,
  variables: AlertActionVariables,
  confirmedMute: ConfirmedMuteEvidence | null,
): DesiredStateTruth {
  switch (variables.kind) {
    case "acknowledge":
      return alert.status === "acknowledged" ? "present" : "absent";
    case "assign":
      return alert.assigned_to === variables.assigneeUserId
        ? "present"
        : "absent";
    case "mute": {
      if (alert.status !== "muted") return "absent";
      const mutedUntil = parseServerTimestamp(alert.muted_until);
      if (confirmedMute?.alertId === variables.alertId) {
        const confirmedUntil = parseServerTimestamp(confirmedMute.mutedUntil);
        if (mutedUntil === null || confirmedUntil === null) return "ambiguous";
        return mutedUntil === confirmedUntil ? "present" : "absent";
      }
      const updatedAt = parseServerTimestamp(alert.updated_at);
      if (updatedAt === null || mutedUntil === null) return "ambiguous";
      return mutedUntil - updatedAt === variables.minutes * 60_000
        ? "present"
        : "ambiguous";
    }
    case "unmute":
      return alert.status !== "muted" ? "present" : "absent";
  }
}

function activeAlertListsAreCoherent(
  queryClient: QueryClient,
  variables: AlertActionVariables,
  exactTruth: DesiredStateTruth,
  confirmedMute: ConfirmedMuteEvidence | null,
): boolean {
  return queryClient
    .getQueriesData<AlertListResponse>({
      queryKey: ["platform", "alerts"],
      type: "active",
    })
    .every(([, data]) =>
      (data?.items ?? []).every(
        (alert) =>
          alert.alert_id !== variables.alertId ||
          actionDesiredStateTruth(alert, variables, confirmedMute) ===
            exactTruth,
      ),
    );
}

function reconciliationResult(
  queryClient: QueryClient,
  variables: AlertActionVariables,
  exactRead: PromiseSettledResult<AlertResponse>,
  invalidationsSucceeded: boolean,
  confirmedMute: ConfirmedMuteEvidence | null,
): ReconciliationResult {
  const current =
    exactRead.status === "fulfilled" &&
    exactRead.value.alert_id === variables.alertId
      ? exactRead.value
      : null;
  const desiredState = current
    ? actionDesiredStateTruth(current, variables, confirmedMute)
    : "ambiguous";
  return Object.freeze({
    activeListsCoherent:
      current !== null &&
      activeAlertListsAreCoherent(
        queryClient,
        variables,
        desiredState,
        confirmedMute,
      ),
    authoritative: current !== null,
    desiredState,
    invalidationsSucceeded,
  });
}

function readExactAlert(queryClient: QueryClient, alertId: string) {
  return queryClient.fetchQuery({
    queryKey: [...EXACT_ALERT_QUERY_NAMESPACE, alertId] as const,
    queryFn: () => getAlert(alertId),
    retry: false,
    staleTime: 0,
  });
}

export function AlertsPage() {
  const queryClient = useQueryClient();
  const recoveryStore = alertRecoveryStoreFor(queryClient);
  const {
    recovery: actionRecovery,
    refreshing: recoveryRefreshing,
    resolution: actionResolution,
  } = useAlertRecovery(queryClient);
  const mounted = useRef(false);
  const consumedResolutionSequence = useRef(actionResolution?.sequence ?? 0);
  const sameTickLock = useRef(false);
  const acceptedAction = useRef<AlertActionVariables | null>(null);
  const dialogSequence = useRef(0);
  const dialogTargetRef = useRef<DialogTarget | null>(null);
  const [statusFilter, setStatusFilter] = useState<Status | "all">("all");
  const [severityFilter, setSeverityFilter] = useState<Severity | "all">("all");
  const [dialogTarget, setDialogTarget] = useState<DialogTarget | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const [muteMinutes, setMuteMinutes] = useState(MUTE_WINDOWS[0].minutes);
  const [actionError, setActionError] = useState<AlertActionError | null>(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (
      !actionResolution ||
      actionResolution.sequence <= consumedResolutionSequence.current
    ) {
      return;
    }
    consumedResolutionSequence.current = actionResolution.sequence;
    const current = dialogTargetRef.current;
    if (
      current?.alertId !== actionResolution.alertId ||
      current.kind !== actionResolution.kind
    ) {
      return;
    }
    dialogTargetRef.current = null;
    setDialogTarget(null);
    setSelectedAssignee(null);
    setMuteMinutes(MUTE_WINDOWS[0].minutes);
    setActionError(null);
  }, [actionResolution]);

  useEffect(() => {
    if (actionRecovery) setActionError(null);
  }, [actionRecovery]);

  const normalizedFilters = useMemo<Readonly<AlertFilters>>(() => {
    const filters: AlertFilters = {};
    if (statusFilter !== "all") filters.status = statusFilter;
    if (severityFilter !== "all") filters.severity = severityFilter;
    return Object.freeze(filters);
  }, [severityFilter, statusFilter]);

  const alertsQueryKey = useMemo(
    () => ["platform", "alerts", normalizedFilters] as const,
    [normalizedFilters],
  );

  const alertsQuery = useQuery({
    queryKey: ["platform", "alerts", normalizedFilters] as const,
    queryFn: ({ queryKey }) => listAlerts(queryKey[2]),
    notifyOnChangeProps: "all",
  });

  const rosterQuery = useQuery({
    queryKey: ["platform", "admin-access", "roster"],
    queryFn: () => listAdminRoster(),
    notifyOnChangeProps: "all",
  });

  const alertsCacheState = useExactQueryState<AlertListResponse>(
    queryClient,
    alertsQueryKey,
  );
  const rosterCacheState = useExactQueryState<AdminRosterResponse>(
    queryClient,
    ROSTER_QUERY_KEY,
  );

  const globalActionCount = useIsMutating({
    mutationKey: ALERT_ACTION_MUTATION_KEY,
    exact: true,
  });

  async function reconcileAction(
    variables: AlertActionVariables,
    confirmedMute: ConfirmedMuteEvidence | null,
  ): Promise<ReconciliationResult> {
    const [alertsReconciliation, auditReconciliation, exactRead] =
      await Promise.allSettled([
        queryClient.invalidateQueries(
          { queryKey: ["platform", "alerts"] },
          { throwOnError: true },
        ),
        queryClient.invalidateQueries(
          { queryKey: ["platform", "audit"] },
          { throwOnError: true },
        ),
        readExactAlert(queryClient, variables.alertId),
      ]);
    return reconciliationResult(
      queryClient,
      variables,
      exactRead,
      alertsReconciliation.status === "fulfilled" &&
        auditReconciliation.status === "fulfilled",
      confirmedMute,
    );
  }

  async function refreshRecoveryAuthority(
    variables: AlertActionVariables,
    confirmedMute: ConfirmedMuteEvidence | null,
  ): Promise<ReconciliationResult> {
    const [alertsRefresh, auditRefresh, exactRead] = await Promise.allSettled([
      queryClient.refetchQueries(
        { queryKey: ["platform", "alerts"], type: "active" },
        { throwOnError: true },
      ),
      queryClient.refetchQueries(
        { queryKey: ["platform", "audit"], type: "active" },
        { throwOnError: true },
      ),
      readExactAlert(queryClient, variables.alertId),
    ]);
    return reconciliationResult(
      queryClient,
      variables,
      exactRead,
      alertsRefresh.status === "fulfilled" &&
        auditRefresh.status === "fulfilled",
      confirmedMute,
    );
  }

  function reconciliationProvesDesiredState(
    reconciliation: ReconciliationResult,
  ) {
    return (
      reconciliation.invalidationsSucceeded &&
      reconciliation.authoritative &&
      reconciliation.desiredState === "present" &&
      reconciliation.activeListsCoherent
    );
  }

  function reconciliationHasAuthority(reconciliation: ReconciliationResult) {
    return (
      reconciliation.invalidationsSucceeded &&
      reconciliation.authoritative &&
      reconciliation.desiredState !== "ambiguous" &&
      reconciliation.activeListsCoherent
    );
  }

  function dialogMatches(variables: AlertActionVariables): boolean {
    if (variables.kind !== "assign" && variables.kind !== "mute") return false;
    const current = dialogTargetRef.current;
    return (
      current?.alertId === variables.alertId &&
      current.kind === variables.kind &&
      current.token === variables.dialogToken
    );
  }

  function beginActionRecovery(
    variables: AlertActionVariables,
    transport: AlertActionRecovery["transport"],
    confirmedMute: ConfirmedMuteEvidence | null,
  ) {
    const recovery: AlertActionRecovery = Object.freeze({
      confirmedMute: transport === "confirmed" ? confirmedMute : null,
      transport,
      variables,
    });
    recoveryStore.getState().recordRecovery(recovery);
    if (mounted.current) setActionError(null);
  }

  const actionMutation = useMutation({
    mutationKey: ["platform", "alerts", "action"],
    mutationFn: (variables: AlertActionVariables) => {
      switch (variables.kind) {
        case "acknowledge":
          return acknowledgeAlert(variables.alertId);
        case "assign":
          return assignAlert(variables.alertId, variables.assigneeUserId);
        case "mute":
          return muteAlert(variables.alertId, { minutes: variables.minutes });
        case "unmute":
          return unmuteAlert(variables.alertId);
      }
    },
    retry: false,
    onSuccess: async (response, variables) => {
      if (acceptedAction.current !== variables) return;
      const confirmedMute = deriveConfirmedMuteEvidence(response, variables);
      const reconciliation = await reconcileAction(variables, confirmedMute);
      if (acceptedAction.current !== variables) return;
      if (reconciliationProvesDesiredState(reconciliation)) {
        recoveryStore.getState().recordResolution(variables);
        if (mounted.current) setActionError(null);
        return;
      }
      beginActionRecovery(variables, "confirmed", confirmedMute);
    },
    onError: async (_error, variables) => {
      if (acceptedAction.current !== variables) return;
      const reconciliation = await reconcileAction(variables, null);
      if (acceptedAction.current !== variables) return;
      if (reconciliationProvesDesiredState(reconciliation)) {
        recoveryStore.getState().recordResolution(variables);
        if (mounted.current) setActionError(null);
        return;
      }
      if (!reconciliationHasAuthority(reconciliation)) {
        beginActionRecovery(variables, "uncertain", null);
        return;
      }
      if (mounted.current) {
        setActionError(
          Object.freeze({
            alertId: variables.alertId,
            kind: variables.kind,
            message: ACTION_OUTCOME_UNCERTAIN,
          }),
        );
      }
    },
    onSettled: (_response, _error, variables) => {
      if (acceptedAction.current !== variables) return;
      acceptedAction.current = null;
      sameTickLock.current = false;
    },
  });

  const actionPending = actionMutation.isPending || globalActionCount > 0;
  const alerts = alertsCacheState?.data?.items ?? [];
  const alertsReadFailed =
    alertsCacheState?.error != null ||
    alertsCacheState?.fetchFailureReason != null ||
    (alertsCacheState?.fetchFailureCount ?? 0) > 0;
  const rosterReadFailed =
    rosterCacheState?.error != null ||
    rosterCacheState?.fetchFailureReason != null ||
    (rosterCacheState?.fetchFailureCount ?? 0) > 0;
  const listAuthoritative = queryIsAuthoritative(exactAlertsQuery());
  const rosterAuthoritative = queryIsAuthoritative(exactRosterQuery());
  const listAuthorityNeedsRefresh =
    alertsCacheState?.data !== undefined &&
    alertsCacheState.fetchStatus === "idle" &&
    !alertsReadFailed &&
    !listAuthoritative;
  const rosterAuthorityNeedsRefresh =
    rosterCacheState?.data !== undefined &&
    rosterCacheState.fetchStatus === "idle" &&
    !rosterReadFailed &&
    !rosterAuthoritative;
  const listControlsLocked =
    !listAuthoritative || actionPending || actionRecovery !== null;
  const hasFilters = statusFilter !== "all" || severityFilter !== "all";

  const eligibleCandidates = useMemo(
    () => (rosterQuery.data?.items ?? []).filter(isEligibleCandidate),
    [rosterQuery.data],
  );
  const duplicateNames = useMemo(
    () => duplicateCandidateNames(eligibleCandidates),
    [eligibleCandidates],
  );
  const selectedCandidate = eligibleCandidates.find(
    (candidate) => candidate.firebase_uid.trim() === selectedAssignee,
  );
  const selectedIdentity = selectedCandidate
    ? candidateIdentity(selectedCandidate, duplicateNames)
    : null;
  const activeDialogError =
    actionError &&
    dialogTarget?.alertId === actionError.alertId &&
    dialogTarget.kind === actionError.kind
      ? actionError
      : null;
  const sharedResolutionMatchesDialog = Boolean(
    actionResolution &&
      actionResolution.sequence > consumedResolutionSequence.current &&
      dialogTarget?.alertId === actionResolution.alertId &&
      dialogTarget.kind === actionResolution.kind,
  );
  const dialogRecoveryLocked =
    actionRecovery !== null || sharedResolutionMatchesDialog;
  const selectedAssigneeWouldChange =
    dialogTarget?.kind === "assign" &&
    selectedAssignee !== null &&
    currentListAlert(dialogTarget.alertId)?.assigned_to !== selectedAssignee;
  const assignmentReady =
    listAuthoritative &&
    rosterAuthoritative &&
    !actionPending &&
    !dialogRecoveryLocked &&
    selectedAssigneeWouldChange &&
    Boolean(selectedIdentity?.selectable);

  function exactAlertsQuery() {
    return queryClient
      .getQueryCache()
      .find<AlertListResponse>({ queryKey: alertsQueryKey, exact: true });
  }

  function exactRosterQuery() {
    return queryClient.getQueryCache().find<AdminRosterResponse>({
      queryKey: ROSTER_QUERY_KEY,
      exact: true,
    });
  }

  function currentListAlert(alertId: string): AlertResponse | null {
    const query = exactAlertsQuery();
    if (!queryIsAuthoritative(query)) return null;
    return (
      (query.state.data?.items ?? []).find(
        (alert) => alert.alert_id === alertId,
      ) ?? null
    );
  }

  function exactAssigneeIsEligible(firebaseUid: string): boolean {
    const query = exactRosterQuery();
    if (!queryIsAuthoritative(query)) return false;
    return (query.state.data?.items ?? []).some(
      (candidate) =>
        isEligibleCandidate(candidate) &&
        candidate.firebase_uid.trim() === firebaseUid,
    );
  }

  function setAuthorityError(variables: AlertActionVariables, message: string) {
    setActionError(
      Object.freeze({
        alertId: variables.alertId,
        kind: variables.kind,
        message,
      }),
    );
  }

  function acceptAction(variables: AlertActionVariables) {
    if (
      recoveryStore.getState().recovery ||
      sameTickLock.current ||
      queryClient.isMutating({
        mutationKey: ALERT_ACTION_MUTATION_KEY,
        exact: true,
      }) > 0
    ) {
      return;
    }

    if (!queryIsAuthoritative(exactAlertsQuery())) {
      setAuthorityError(variables, ALERTS_REFRESH_REQUIRED);
      return;
    }
    const current = currentListAlert(variables.alertId);
    if (!current || !isActionAllowed(current.status, variables.kind)) return;

    if (variables.kind === "assign") {
      if (!dialogMatches(variables)) return;
      if (current.assigned_to === variables.assigneeUserId) return;
      if (!queryIsAuthoritative(exactRosterQuery())) {
        setAuthorityError(variables, ROSTER_REFRESH_REQUIRED);
        return;
      }
      if (!exactAssigneeIsEligible(variables.assigneeUserId)) return;
    }
    if (
      variables.kind === "mute" &&
      (!dialogMatches(variables) ||
        !MUTE_WINDOWS.some((window) => window.minutes === variables.minutes))
    ) {
      return;
    }

    sameTickLock.current = true;
    acceptedAction.current = variables;
    setActionError(null);
    actionMutation.mutate(variables);
  }

  function openDialog(alert: AlertResponse, kind: DialogTarget["kind"]) {
    if (
      recoveryStore.getState().recovery ||
      actionPending ||
      dialogTargetRef.current ||
      !listAuthoritative ||
      !isActionAllowed(alert.status, kind)
    ) {
      return;
    }
    if (!queryIsAuthoritative(exactAlertsQuery())) {
      setActionError(
        Object.freeze({
          alertId: alert.alert_id,
          kind,
          message: ALERTS_REFRESH_REQUIRED,
        }),
      );
      return;
    }
    if (currentListAlert(alert.alert_id)?.status !== alert.status) return;
    const target = Object.freeze({
      alertId: alert.alert_id,
      kind,
      title: alert.title,
      token: ++dialogSequence.current,
    });
    dialogTargetRef.current = target;
    setDialogTarget(target);
    setSelectedAssignee(null);
    setMuteMinutes(MUTE_WINDOWS[0].minutes);
    setActionError(null);
  }

  async function refreshAlertsAuthority() {
    if (recoveryStore.getState().recovery) return;
    const recovery = actionError;
    try {
      await alertsQuery.refetch({ throwOnError: true });
    } catch {
      return;
    }
    if (!mounted.current || !queryIsAuthoritative(exactAlertsQuery())) return;
    setActionError((current) => (current === recovery ? null : current));
  }

  async function retryAlertsRead() {
    try {
      await alertsQuery.refetch({ throwOnError: true });
    } catch {
      // The active query renders its retained or terminal read error.
    }
  }

  async function refreshRosterAuthority() {
    if (recoveryStore.getState().recovery) return;
    const recovery = actionError;
    const result = await rosterQuery.refetch();
    if (
      !mounted.current ||
      result.error != null ||
      !queryIsAuthoritative(exactRosterQuery())
    ) {
      return;
    }
    setActionError((current) => (current === recovery ? null : current));
  }

  async function refreshActionRecovery() {
    const recovery = recoveryStore.getState().recovery;
    if (!recovery || !recoveryStore.getState().tryStartRefresh(recovery)) {
      return;
    }
    try {
      const reconciliation = await refreshRecoveryAuthority(
        recovery.variables,
        recovery.confirmedMute,
      );
      if (recoveryStore.getState().recovery !== recovery) return;

      const desiredStateProven =
        reconciliationProvesDesiredState(reconciliation);
      const didNotApplyProven =
        recovery.transport === "uncertain" &&
        reconciliationHasAuthority(reconciliation) &&
        reconciliation.desiredState === "absent";
      if (!desiredStateProven && !didNotApplyProven) return;

      const resolved = desiredStateProven
        ? recoveryStore.getState().resolveRecovery(recovery)
        : recoveryStore.getState().clearRecovery(recovery);
      if (!resolved || !mounted.current) return;
      setActionError(null);
    } finally {
      recoveryStore.getState().finishRefresh(recovery);
    }
  }

  function closeDialog() {
    if (actionPending || recoveryStore.getState().recovery) {
      return;
    }
    dialogTargetRef.current = null;
    setDialogTarget(null);
    setSelectedAssignee(null);
    setMuteMinutes(MUTE_WINDOWS[0].minutes);
    setActionError(null);
  }

  function submitAssignment() {
    const target = dialogTargetRef.current;
    if (target?.kind !== "assign" || !selectedAssignee || !assignmentReady) {
      return;
    }
    acceptAction(
      Object.freeze({
        alertId: target.alertId,
        assigneeUserId: selectedAssignee,
        dialogToken: target.token,
        kind: "assign",
      }),
    );
  }

  function submitMute() {
    const target = dialogTargetRef.current;
    if (
      target?.kind !== "mute" ||
      !listAuthoritative ||
      recoveryStore.getState().recovery
    ) {
      return;
    }
    acceptAction(
      Object.freeze({
        alertId: target.alertId,
        dialogToken: target.token,
        kind: "mute",
        minutes: muteMinutes,
      }),
    );
  }

  const dialogNeedsAlertsRefresh =
    listAuthorityNeedsRefresh ||
    activeDialogError?.message === ALERTS_REFRESH_REQUIRED;
  const dialogNeedsRosterRefresh =
    dialogTarget?.kind === "assign" &&
    (rosterAuthorityNeedsRefresh ||
      activeDialogError?.message === ROSTER_REFRESH_REQUIRED);
  const dialogErrorMessage = actionRecovery
    ? RECONCILIATION_REQUIRED
    : dialogNeedsAlertsRefresh
      ? ALERTS_REFRESH_REQUIRED
      : dialogNeedsRosterRefresh
        ? ROSTER_REFRESH_REQUIRED
        : (activeDialogError?.message ?? null);
  const dialogErrorRefreshing = actionRecovery
    ? recoveryRefreshing
    : dialogNeedsRosterRefresh
      ? rosterQuery.isFetching
      : alertsQuery.isFetching;
  const refreshDialogError = actionRecovery
    ? () => void refreshActionRecovery()
    : dialogNeedsAlertsRefresh
      ? () => void refreshAlertsAuthority()
      : dialogNeedsRosterRefresh
        ? () => void refreshRosterAuthority()
        : null;
  const pageNeedsAlertsRefresh =
    listAuthorityNeedsRefresh ||
    actionError?.message === ALERTS_REFRESH_REQUIRED;
  const pageErrorMessage = dialogTarget
    ? null
    : actionRecovery
      ? RECONCILIATION_REQUIRED
      : pageNeedsAlertsRefresh
        ? ALERTS_REFRESH_REQUIRED
        : (actionError?.message ?? null);
  const refreshPageError = actionRecovery
    ? () => void refreshActionRecovery()
    : pageNeedsAlertsRefresh
      ? () => void refreshAlertsAuthority()
      : null;
  const pageErrorRefreshing = actionRecovery
    ? recoveryRefreshing
    : alertsQuery.isFetching;
  const dialogActionLocked = actionPending || dialogRecoveryLocked;
  const muteReady =
    listAuthoritative && !actionPending && !dialogRecoveryLocked;

  return (
    <Stack spacing={3}>
      <Box component="header">
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Alerts
        </Typography>
      </Box>

      <AlertFilterBar
        locked={listControlsLocked}
        severity={severityFilter}
        status={statusFilter}
        onSeverityChange={setSeverityFilter}
        onStatusChange={setStatusFilter}
      />

      {pageErrorMessage ? (
        <Alert
          severity="error"
          role="alert"
          action={
            refreshPageError ? (
              <Button
                color="inherit"
                size="small"
                disabled={pageErrorRefreshing || actionPending}
                onClick={refreshPageError}
              >
                Refresh
              </Button>
            ) : undefined
          }
        >
          {pageErrorMessage}
        </Alert>
      ) : null}

      {alertsQuery.isPending && !alertsQuery.data ? (
        <Box
          role="status"
          aria-label="Loading alerts"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress size={36} />
        </Box>
      ) : alertsReadFailed && !alertsQuery.data ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button
              color="inherit"
              size="small"
              disabled={alertsQuery.isFetching || actionPending}
              onClick={() => void retryAlertsRead()}
            >
              Retry
            </Button>
          }
        >
          Alerts could not be loaded.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {alertsQuery.isFetching ? (
            <Box
              role="status"
              aria-label="Refreshing alerts"
              aria-live="polite"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <CircularProgress size={18} />
              <Typography color="text.secondary" variant="caption">
                Refreshing alerts
              </Typography>
            </Box>
          ) : null}

          {alertsReadFailed ? (
            <Alert
              severity="error"
              role="alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={alertsQuery.isFetching || actionPending}
                  onClick={() => void retryAlertsRead()}
                >
                  Retry
                </Button>
              }
            >
              The alert list may be out of date.
            </Alert>
          ) : null}

          <AlertQueue
            alerts={alerts}
            busy={alertsQuery.isFetching}
            filtered={hasFilters}
            locked={listControlsLocked}
            roster={rosterQuery.data}
            total={alertsQuery.data?.total}
            onAcknowledge={(alert) =>
              acceptAction(
                Object.freeze({
                  alertId: alert.alert_id,
                  kind: "acknowledge",
                }),
              )
            }
            onAssign={(alert) => openDialog(alert, "assign")}
            onMute={(alert) => openDialog(alert, "mute")}
            onUnmute={(alert) =>
              acceptAction(
                Object.freeze({
                  alertId: alert.alert_id,
                  kind: "unmute",
                }),
              )
            }
          />
        </Stack>
      )}

      <AssignAlertDialog
        actionError={dialogErrorMessage}
        actionErrorRefreshing={dialogErrorRefreshing}
        actionLocked={dialogActionLocked}
        actionPending={actionPending}
        assignmentReady={assignmentReady}
        candidates={eligibleCandidates}
        duplicateNames={duplicateNames}
        open={dialogTarget?.kind === "assign" && !sharedResolutionMatchesDialog}
        rosterError={rosterReadFailed}
        rosterFetching={rosterQuery.isFetching}
        rosterPending={rosterQuery.isPending}
        selectedAssignee={selectedAssignee}
        target={dialogTarget}
        onAssign={submitAssignment}
        onClose={closeDialog}
        onRefreshActionError={refreshDialogError}
        onRetryRoster={() => void rosterQuery.refetch()}
        onSelectAssignee={setSelectedAssignee}
      />

      <MuteAlertDialog
        actionError={dialogErrorMessage}
        actionErrorRefreshing={dialogErrorRefreshing}
        actionLocked={dialogActionLocked}
        actionPending={actionPending}
        actionReady={muteReady}
        minutes={muteMinutes}
        open={dialogTarget?.kind === "mute" && !sharedResolutionMatchesDialog}
        target={dialogTarget}
        onClose={closeDialog}
        onMinutesChange={setMuteMinutes}
        onMute={submitMute}
        onRefreshActionError={refreshDialogError}
      />
    </Stack>
  );
}
